// useBodyScrollLock — locks the document body while a modal/sheet is open.
//
// Why this exists:
// On iOS WKWebView (Capacitor) and Safari, opening a `position:fixed`
// modal does NOT prevent the page beneath from scrolling. When the user
// scrolls inside the modal and hits the top/bottom, momentum bleeds
// through to the body, leaving the page in a different scroll position
// than where the user opened the modal. On dismissal the user lands at
// the bottom of the page and has to scroll back up — exactly the "view
// is way at the bottom" complaint.
//
// This hook freezes the body at its current scroll position while
// `active` is true, then restores that position when the modal closes.
import { useEffect } from "react";

export function useBodyScrollLock(active) {
    useEffect(() => {
        if (!active) return;
        const y = window.scrollY || window.pageYOffset || 0;
        const body = document.body;
        const html = document.documentElement;

        // Snapshot styles so we can restore exactly what was there before.
        const prev = {
            position: body.style.position,
            top: body.style.top,
            left: body.style.left,
            right: body.style.right,
            width: body.style.width,
            overflow: html.style.overflow,
        };

        body.style.position = "fixed";
        body.style.top = `-${y}px`;
        body.style.left = "0";
        body.style.right = "0";
        body.style.width = "100%";
        html.style.overflow = "hidden";

        return () => {
            body.style.position = prev.position;
            body.style.top = prev.top;
            body.style.left = prev.left;
            body.style.right = prev.right;
            body.style.width = prev.width;
            html.style.overflow = prev.overflow;
            // Restore the exact scroll position the user was at.
            window.scrollTo(0, y);
        };
    }, [active]);
}

export default useBodyScrollLock;
